import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { deliveryControls } from '@bb/contracts';
import { Button, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, ReadOnlyBanner, Select, Switch } from '@bb/ui';
import { ApiError, type FrequencyCapRuleBody, type FrequencyCapRuleView, type TagSummary } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { NumberInput } from '../campaigns/trigger/NumberInput.tsx';

type CapChannel = deliveryControls.CapChannel;
type WindowUnit = deliveryControls.CapWindow['unit'];

const MAX_ACTIVE = deliveryControls.FREQUENCY_CAP_MAX_ACTIVE;
const MAX_WINDOW_DAYS = deliveryControls.FREQUENCY_CAP_MAX_WINDOW_DAYS;
const CHANNEL_LABELS: Record<CapChannel, string> = { push: 'Push', webhook: 'Webhook' };
const UNITS: Array<{ value: WindowUnit; label: string }> = [
  { value: 'minutes', label: 'minutes' },
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
];
const SINGULAR: Record<WindowUnit, string> = { minutes: 'minute', days: 'day', weeks: 'week' };

/** The rule as the dialog edits it: number fields may be empty while typing. */
interface Draft {
  name: string;
  channels: CapChannel[];
  tagId: string | null;
  maxCount: number | undefined;
  windowValue: number | undefined;
  windowUnit: WindowUnit;
  enabled: boolean;
}

/** A new rule starts as «at most 3 push messages per day», the acceptance example of BB-102. */
const newDraft = (enabled: boolean): Draft => ({ name: '', channels: ['push'], tagId: null, maxCount: 3, windowValue: 1, windowUnit: 'days', enabled });
const toDraft = (rule: FrequencyCapRuleView): Draft => ({ name: rule.name, channels: [...rule.channels], tagId: rule.tagId, maxCount: rule.maxCount, windowValue: rule.window.value, windowUnit: rule.window.unit, enabled: rule.enabled });
const toBody = (d: Draft) => ({ name: d.name, channels: d.channels, tagId: d.tagId, maxCount: d.maxCount, window: { value: d.windowValue, unit: d.windowUnit }, enabled: d.enabled });

const MESSAGES: Record<string, string> = {
  name: 'Enter a name of up to 120 characters.',
  channels: 'Choose at least one channel.',
  tagId: 'Choose a tag of this environment.',
  maxCount: 'Enter a whole number from 1 to 10,000.',
  windowValue: 'Enter a whole number from 1.',
  window: `A window is at most ${MAX_WINDOW_DAYS} days.`,
};

/** Client-side check of the contract (`deliveryControls.FrequencyCapRuleBody`): the body to send, or one message per field. */
function checkCapDraft(draft: Draft): { body: FrequencyCapRuleBody | null; issues: Record<string, string> } {
  const parsed = deliveryControls.FrequencyCapRuleBody.safeParse(toBody(draft));
  if (parsed.success) return { body: parsed.data, issues: {} };
  const issues: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const [field, sub] = issue.path;
    const key = field === 'window' ? (sub === 'value' ? 'windowValue' : 'window') : String(field ?? 'rule');
    issues[key] ??= MESSAGES[key] ?? issue.message;
  }
  return { body: null, issues };
}

/** «At most 3 push messages per day», «… per 7 days». */
export function describeCap(rule: Pick<FrequencyCapRuleView, 'channels' | 'maxCount' | 'window'>): string {
  const channels = rule.channels.map((c) => CHANNEL_LABELS[c].toLowerCase()).join(' or ');
  const per = rule.window.value === 1 ? SINGULAR[rule.window.unit] : `${rule.window.value} ${rule.window.unit}`;
  return `At most ${rule.maxCount} ${channels} ${rule.maxCount === 1 ? 'message' : 'messages'} per ${per}`;
}

const tagText = (tagId: string | null, tags: TagSummary[] | undefined) => {
  if (!tagId) return 'All messages';
  const tag = tags?.find((t) => t.id === tagId);
  return tag ? `Only messages tagged ${tag.name}` : 'Only messages with a tag that no longer exists';
};

const errorText = (error: unknown) => {
  if (!(error instanceof ApiError)) return 'Something went wrong.';
  if (error.code === 'frequency_cap_limit') return `At most ${MAX_ACTIVE} rules can be enabled at a time. Disable another rule first.`;
  if (error.code === 'tag_not_found') return 'The tag no longer exists. Choose another tag or none.';
  return (error.problem.issues as Array<{ path: string; message: string }> | undefined)?.map((i) => `${i.path}: ${i.message}`).join('; ') || error.message;
};

/** The rule being added or edited; `title` is fixed when the dialog opens, so it does not follow the name being typed. */
interface Editing {
  id: string | null;
  title: string;
  draft: Draft;
}

function CapDialog({ editing, tags, busy, error, onChange, onCancel, onSave }: {
  editing: Editing | null;
  tags: TagSummary[];
  busy: boolean;
  error: unknown;
  onChange: (patch: Partial<Draft>) => void;
  onCancel: () => void;
  onSave: (body: FrequencyCapRuleBody) => void;
}) {
  const checked = editing ? checkCapDraft(editing.draft) : null;
  return (
    <Modal
      open={editing !== null}
      onOpenChange={(open) => !open && onCancel()}
      title={editing?.title ?? ''}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!checked?.body} onClick={() => checked?.body && onSave(checked.body)}>
            Save
          </Button>
        </>
      }
    >
      {editing && checked && <CapFields draft={editing.draft} issues={checked.issues} tags={tags} onChange={onChange} />}
      {error != null && <p role="alert">{errorText(error)}</p>}
    </Modal>
  );
}

function CapFields({ draft, issues, tags, onChange }: { draft: Draft; issues: Record<string, string>; tags: TagSummary[]; onChange: (patch: Partial<Draft>) => void }) {
  const calendar = draft.windowUnit !== 'minutes';
  return (
    <>
      <Field label="Name" error={issues.name}>
        <Input value={draft.name} onChange={(e) => onChange({ name: e.target.value })} />
      </Field>
      <fieldset>
        <legend>Channels</legend>
        {deliveryControls.CAP_CHANNELS.map((channel) => (
          <Checkbox
            key={channel}
            label={CHANNEL_LABELS[channel]}
            checked={draft.channels.includes(channel)}
            onCheckedChange={(on) => onChange({ channels: on ? deliveryControls.CAP_CHANNELS.filter((c) => c === channel || draft.channels.includes(c)) : draft.channels.filter((c) => c !== channel) })}
          />
        ))}
        <p className="hint">In-app messages and content cards never count towards caps. A push counts once per user, however many devices get it.</p>
        {issues.channels && (
          <p className="bb-field__error" role="alert">
            {issues.channels}
          </p>
        )}
      </fieldset>
      <Field label="Tag" hint="With a tag, only messages of campaigns and Canvas with it count and are capped." error={issues.tagId}>
        <Select options={[{ value: '', label: 'Any message' }, ...tags.map((t) => ({ value: t.id, label: t.name }))]} value={draft.tagId ?? ''} onChange={(e) => onChange({ tagId: e.target.value || null })} />
      </Field>
      <Field label="At most" hint="Messages per user in the window." error={issues.maxCount}>
        <NumberInput min={1} max={10_000} step={1} value={draft.maxCount} onValueChange={(maxCount) => onChange({ maxCount })} />
      </Field>
      <div className="audience-filter">
        <Field label="Per" error={issues.windowValue ?? issues.window}>
          <NumberInput min={1} step={1} value={draft.windowValue} onValueChange={(windowValue) => onChange({ windowValue })} />
        </Field>
        <Field label="Unit">
          <Select options={UNITS} value={draft.windowUnit} onChange={(e) => onChange({ windowUnit: e.target.value as WindowUnit })} />
        </Field>
      </div>
      <p className="hint">
        {calendar ? "Days and weeks are calendar days and weeks in the user's time zone." : 'Minutes slide: the window always ends now.'} At most {MAX_WINDOW_DAYS} days.
      </p>
      <Checkbox label="Enabled" checked={draft.enabled} onCheckedChange={(enabled) => onChange({ enabled })} />
    </>
  );
}

/**
 * Settings → Frequency capping rules (BB-102, CMP-006): how many messages one user may get across campaigns and Canvas
 * — at most N messages of some channels (optionally of one tag) per window. Every enabled rule applies; the first one
 * reached skips the send. Up to ten rules can be enabled; environment managers change them, everyone else reads them.
 */
export function FrequencyCapsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/frequency-caps' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/frequency-caps' });
  const queryClient = useQueryClient();
  const canEdit = usePermissions().has('env.manage');
  const key = ['frequency-caps', environmentId];
  const caps = useQuery({ queryKey: key, queryFn: () => api.listFrequencyCaps(environmentId) });
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<FrequencyCapRuleView | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: FrequencyCapRuleBody }) => (id ? api.updateFrequencyCap(environmentId, id, body) : api.createFrequencyCap(environmentId, body)),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const toggle = useMutation({ mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.updateFrequencyCap(environmentId, id, { enabled }), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteFrequencyCap(environmentId, id),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });
  const open = (next: Editing) => {
    save.reset();
    setEditing(next);
  };

  if (caps.isError) return <ErrorState title="Frequency capping rules could not be loaded" message={errorText(caps.error)} onRetry={() => void caps.refetch()} />;
  if (!caps.data) return <p aria-busy="true">Loading…</p>;
  const rules = caps.data.items;
  const active = rules.filter((r) => r.enabled).length;
  return (
    <section>
      <h1>Frequency capping rules</h1>
      <p className="hint">
        Limits how many messages one user gets from all campaigns and Canvas together. Every enabled rule applies: a send that would go over one is skipped, not delayed. Transactional messages are never capped and do not count.
      </p>
      {!canEdit && <ReadOnlyBanner>Frequency capping rules are changed by admins (env.manage).</ReadOnlyBanner>}
      <p role="status">
        {active} of {MAX_ACTIVE} rules enabled.
      </p>
      {active >= MAX_ACTIVE && <p className="notice notice--warning">{MAX_ACTIVE} rules are enabled, the most allowed. Disable a rule before enabling another one.</p>}
      {canEdit && (
        <Button variant="primary" onClick={() => open({ id: null, title: 'Add rule', draft: newDraft(active < MAX_ACTIVE) })}>
          Add rule
        </Button>
      )}
      {rules.length === 0 ? (
        <EmptyState title="No frequency capping rules" description="Users can get any number of messages." />
      ) : (
        <table className="table" aria-label="Frequency capping rules">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Rule</th>
              <th scope="col">Counts</th>
              <th scope="col">Status</th>
              {canEdit && (
                <th scope="col">
                  <span className="bb-visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.name}</td>
                <td>{describeCap(rule)}</td>
                <td>{tagText(rule.tagId, tags.data)}</td>
                <td>
                  {canEdit ? (
                    <Switch label="Enabled" checked={rule.enabled} disabled={toggle.isPending} onCheckedChange={(enabled) => toggle.mutate({ id: rule.id, enabled })} />
                  ) : rule.enabled ? (
                    'Enabled'
                  ) : (
                    'Disabled'
                  )}
                </td>
                {canEdit && (
                  <td>
                    <Button variant="ghost" aria-label={`Edit ${rule.name}`} onClick={() => open({ id: rule.id, title: `Edit ${rule.name}`, draft: toDraft(rule) })}>
                      Edit
                    </Button>
                    <Button variant="ghost" aria-label={`Delete ${rule.name}`} onClick={() => setRemoving(rule)}>
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {toggle.isError && <p role="alert">{errorText(toggle.error)}</p>}
      <CapDialog
        editing={editing}
        tags={tags.data ?? []}
        busy={save.isPending}
        error={save.error}
        onChange={(patch) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e))}
        onCancel={() => setEditing(null)}
        onSave={(body) => editing && save.mutate({ id: editing.id, body })}
      />
      <ConfirmDialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)} title={`Delete «${removing?.name ?? ''}»?`} confirmLabel="Delete" busy={remove.isPending} onConfirm={() => removing && remove.mutate(removing.id)}>
        Sends stop being checked against this rule right away.
        {remove.isError && <span role="alert"> {errorText(remove.error)}</span>}
      </ConfirmDialog>
    </section>
  );
}
